import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Boxes, Check, Search, Bot, Package } from "lucide-react";
import { SkillsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { cn } from "../lib/cn";
import type { Skill } from "../lib/types";

type Axis = "agent" | "skill";

interface ListEntry {
  id: string;
  primary: string;
  secondary?: string;
  count: number;       // linked count (skills for an agent / agents for a skill)
}

export default function Skills() {
  const qc = useQueryClient();
  const toast = useToast();

  const [axis, setAxis] = useState<Axis>("agent");
  const [selected, setSelected] = useState<string | null>(null);
  const [listQuery, setListQuery] = useState("");

  const { data: skills = [], isLoading: lSkills } = useQuery({ queryKey: qk.skills.list, queryFn: SkillsApi.list });
  const { data: agents = [], isLoading: lAgents } = useQuery({ queryKey: qk.skills.fleetAgents, queryFn: SkillsApi.fleetAgents });
  const { data: assignments = {}, isLoading: lAssign } = useQuery({ queryKey: qk.skills.assignments, queryFn: SkillsApi.assignments });

  // Reset selection when flipping axis (an agent id won't match a skill id).
  function flipAxis(next: Axis) {
    if (next === axis) return;
    setAxis(next);
    setSelected(null);
    setListQuery("");
  }

  const toggle = useMutation({
    mutationFn: ({ agent, skill, on }: { agent: string; skill: string; on: boolean }) =>
      on ? SkillsApi.enable(agent, skill) : SkillsApi.disable(agent, skill),
    onMutate: async ({ agent, skill, on }) => {
      await qc.cancelQueries({ queryKey: qk.skills.assignments });
      const prev = qc.getQueryData<Record<string, string[]>>(qk.skills.assignments);
      qc.setQueryData<Record<string, string[]>>(qk.skills.assignments, (old) => {
        const m = { ...(old ?? {}) };
        m[agent] = on
          ? [...new Set([...(m[agent] ?? []), skill])].sort()
          : (m[agent] ?? []).filter((s) => s !== skill);
        return m;
      });
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.skills.assignments, ctx.prev);
      toast.error("Update failed", String(err));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.skills.assignments }),
  });

  const isAssigned = (agent: string, skill: string) => Boolean(assignments[agent]?.includes(skill));

  // Build the list entries for the active axis.
  const entries: ListEntry[] = useMemo(() => {
    if (axis === "agent") {
      return agents
        .map((a) => ({ id: a, primary: a, count: (assignments[a] ?? []).length }))
        .sort((x, y) => x.primary.localeCompare(y.primary));
    }
    return skills
      .map((s) => ({
        id: s.name,
        primary: s.name,
        secondary: s.description,
        count: agents.filter((a) => isAssigned(a, s.name)).length,
      }))
      .sort((x, y) => x.primary.localeCompare(y.primary));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axis, agents, skills, assignments]);

  const filtered = entries.filter((e) =>
    !listQuery ||
    e.primary.toLowerCase().includes(listQuery.toLowerCase()) ||
    (e.secondary?.toLowerCase().includes(listQuery.toLowerCase()) ?? false),
  );

  if (lSkills || lAgents || lAssign) return <CenteredSpinner />;

  const totalAssignments = Object.values(assignments).flat().length;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Skills"
        icon={Boxes}
        subtitle={`${skills.length} skills · ${agents.length} agents · ${totalAssignments} assignments`}
        actions={
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
            <button
              onClick={() => flipAxis("agent")}
              className={cn("px-2.5 h-7 inline-flex items-center gap-1.5", axis === "agent" ? "bg-surface-2 text-fg" : "text-fg-muted hover:text-fg")}
            >
              <Bot className="size-3.5" /> By agent
            </button>
            <button
              onClick={() => flipAxis("skill")}
              className={cn("px-2.5 h-7 inline-flex items-center gap-1.5 border-l border-border", axis === "skill" ? "bg-surface-2 text-fg" : "text-fg-muted hover:text-fg")}
            >
              <Package className="size-3.5" /> By skill
            </button>
          </div>
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<ListEntry>
          items={filtered}
          itemKey={(e) => e.id}
          selectedId={selected}
          onSelect={setSelected}
          itemHeight={axis === "skill" ? 60 : 52}
          toolbar={
            <div className="relative px-3 py-2">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
              <input
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder={axis === "agent" ? "search agents…" : "search skills…"}
                className="w-full pl-7 h-7 text-xs"
              />
            </div>
          }
          emptyState={<EmptyState icon={<Boxes className="size-8" />} title={`No ${axis}s match`} />}
          renderItem={({ item }) => (
            <div className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                {axis === "agent"
                  ? <Bot className="size-3.5 text-fg-subtle flex-shrink-0" />
                  : <Package className="size-3.5 text-fg-subtle flex-shrink-0" />}
                <span className="text-sm font-medium text-fg truncate">{item.primary}</span>
                <Pill tone={item.count > 0 ? "info" : "neutral"} className="ml-auto">
                  {item.count} {axis === "agent" ? "skills" : "agents"}
                </Pill>
              </div>
              {item.secondary && <div className="text-xs text-fg-subtle truncate mt-0.5">{item.secondary}</div>}
            </div>
          )}
          renderDetail={(item) => (
            <AssignmentDetail
              key={`${axis}:${item.id}`}
              axis={axis}
              entryId={item.id}
              skills={skills}
              agents={agents}
              isAssigned={isAssigned}
              onToggle={(agent, skill, on) => toggle.mutate({ agent, skill, on })}
            />
          )}
        />
      </div>
    </div>
  );
}

// Detail pane: the "other axis" as a searchable toggle list. For an agent,
// that's the skills it has; for a skill, the agents that have it.
function AssignmentDetail({
  axis,
  entryId,
  skills,
  agents,
  isAssigned,
  onToggle,
}: {
  axis: Axis;
  entryId: string;
  skills: Skill[];
  agents: string[];
  isAssigned: (agent: string, skill: string) => boolean;
  onToggle: (agent: string, skill: string, on: boolean) => void;
}) {
  const [q, setQ] = useState("");
  const [showAssignedOnly, setShowAssignedOnly] = useState(false);
  useEffect(() => { setQ(""); setShowAssignedOnly(false); }, [entryId]);

  // Rows of the other axis: { id, label, description?, linked }
  const rows = useMemo(() => {
    if (axis === "agent") {
      // entry is an agent → list skills, linked = agent has skill
      return skills.map((s) => ({
        id: s.name,
        label: s.name,
        description: s.description,
        linked: isAssigned(entryId, s.name),
        toggle: (on: boolean) => onToggle(entryId, s.name, on),
      }));
    }
    // entry is a skill → list agents, linked = agent has this skill
    return agents.map((a) => ({
      id: a,
      label: a,
      description: undefined as string | undefined,
      linked: isAssigned(a, entryId),
      toggle: (on: boolean) => onToggle(a, entryId, on),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axis, entryId, skills, agents, isAssigned]);

  const linkedCount = rows.filter((r) => r.linked).length;
  const filtered = rows
    .filter((r) => !showAssignedOnly || r.linked)
    .filter((r) => !q || r.label.toLowerCase().includes(q.toLowerCase()) || (r.description?.toLowerCase().includes(q.toLowerCase()) ?? false));

  const otherLabel = axis === "agent" ? "skill" : "agent";

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          {axis === "agent" ? <Bot className="size-4 text-fg-subtle" /> : <Package className="size-4 text-fg-subtle" />}
          <h2 className="text-base font-semibold text-fg">{entryId}</h2>
          <Pill tone="info" className="ml-auto">{linkedCount} {otherLabel}{linkedCount === 1 ? "" : "s"}</Pill>
        </div>
        <p className="text-xs text-fg-muted mt-1">
          {axis === "agent"
            ? "Toggle a skill to symlink it into this agent's .claude/skills/."
            : "Toggle to symlink this skill into an agent's .claude/skills/."}
        </p>
      </div>

      <div className="px-5 py-2.5 border-b border-border flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`search ${otherLabel}s…`}
            className="w-full pl-8 h-7 text-xs"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted cursor-pointer whitespace-nowrap">
          <input type="checkbox" checked={showAssignedOnly} onChange={(e) => setShowAssignedOnly(e.target.checked)} className="size-3.5" />
          assigned only
        </label>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-border">
        {filtered.length === 0 && (
          <div className="p-6"><EmptyState icon={<Boxes className="size-7" />} title="Nothing matches" /></div>
        )}
        {filtered.map((r) => (
          <button
            key={r.id}
            onClick={() => r.toggle(!r.linked)}
            className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-surface-2 transition-colors ring-focus"
          >
            <span
              className={cn(
                "size-5 rounded inline-flex items-center justify-center flex-shrink-0 transition-colors",
                r.linked ? "bg-accent text-fg-inverse" : "bg-surface-2 border border-border",
              )}
            >
              {r.linked && <Check className="size-3" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-fg truncate">{r.label}</span>
              {r.description && <span className="block text-xs text-fg-subtle truncate">{r.description}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
